import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Wifi, WifiOff, Plus, Copy, RefreshCw, Clock,
  Server, Loader2, Globe, Camera, Key
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";

export default function ConnectorsPage() {
  const { user, isSuperAdmin } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [form, setForm] = useState({ name: '', organization_id: '', site_id: '', version: '1.0.0' });

  useEffect(() => {
    fetchData();
    // Initial load only; role changes require a fresh authenticated session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requests = [axios.get(`${API}/connectors`, { withCredentials: true })];
      if (isSuperAdmin()) {
        requests.push(axios.get(`${API}/admin/organizations`, { withCredentials: true }));
        requests.push(axios.get(`${API}/admin/sites`, { withCredentials: true }));
      }
      const responses = await Promise.all(requests);
      setConnectors(responses[0].data);
      if (responses[1]) setOrgs(responses[1].data);
      if (responses[2]) setSites(responses[2].data);
    } catch {} finally { setIsLoading(false); }
  };

  const openCreate = () => {
    setForm({
      name: '', version: '1.0.0',
      organization_id: user?.organization_id || '',
      site_id: user?.site_id || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('El nombre es obligatorio'); return; }
    if (!form.organization_id) { toast.error('Selecciona una organizacion'); return; }
    if (!form.site_id) { toast.error('Selecciona una sede'); return; }
    setIsSaving(true);
    try {
      const { data } = await axios.post(`${API}/connectors/register`, form, { withCredentials: true });
      toast.success(`Conector registrado. License Key: ${data.license_key}`);
      setShowModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setIsSaving(false); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('License key copiada');
  };

  const formatTimeAgo = (isoStr) => {
    if (!isoStr) return 'Nunca';
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'Hace segundos';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
    return `Hace ${Math.floor(diff / 86400)} dias`;
  };

  const isOnline = (c) => {
    if (c.status !== 'online') return false;
    if (!c.last_seen_at) return false;
    const diff = (Date.now() - new Date(c.last_seen_at).getTime()) / 1000;
    return diff < 300; // 5 min threshold
  };

  const filteredSites = sites.filter(s => s.organization_id === form.organization_id);

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button></Link>
            <div className="border-l border-slate-700 pl-4">
              <h1 className="text-lg font-bold text-white">Edge Connectors</h1>
              <p className="text-sm text-slate-400">Conectores locales para procesamiento de video</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchData} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-connectors"><RefreshCw className="w-4 h-4 mr-2" />Refrescar</Button>
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-connector-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Conector</Button>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Server className="w-5 h-5 text-blue-400" /></div>
              <div><p className="text-sm text-slate-400">Total</p><p className="text-2xl font-bold text-white">{connectors.length}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><Wifi className="w-5 h-5 text-emerald-400" /></div>
              <div><p className="text-sm text-slate-400">Online</p><p className="text-2xl font-bold text-emerald-400">{connectors.filter(isOnline).length}</p></div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center"><WifiOff className="w-5 h-5 text-red-400" /></div>
              <div><p className="text-sm text-slate-400">Offline</p><p className="text-2xl font-bold text-red-400">{connectors.filter(c => !isOnline(c)).length}</p></div>
            </CardContent>
          </Card>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : connectors.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Server className="w-16 h-16 mx-auto mb-4 opacity-40" />
            <p className="text-lg">No hay conectores registrados</p>
            <p className="text-sm mt-1">Registra un Edge Connector para procesar video local</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {connectors.map((c) => {
              const online = isOnline(c);
              return (
                <Card key={c.id} className={`bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-600 transition-colors ${selectedConnector?.id === c.id ? 'border-blue-500' : ''}`}
                  onClick={() => setSelectedConnector(selectedConnector?.id === c.id ? null : c)} data-testid={`connector-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${online ? 'bg-emerald-500/20' : 'bg-slate-700'}`}>
                          <Server className={`w-6 h-6 ${online ? 'text-emerald-400' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <p className="text-white font-semibold text-lg">{c.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={online ? 'bg-emerald-600' : 'bg-red-600'}>{online ? 'Online' : 'Offline'}</Badge>
                            {c.version && <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">v{c.version}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Camera className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400 text-sm">{c.cameras_active || 0}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatTimeAgo(c.last_seen_at)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <Globe className="w-3.5 h-3.5" />
                        <span>{c.local_ip || 'Sin IP'}</span>
                      </div>
                    </div>
                    {selectedConnector?.id === c.id && (
                      <div className="mt-4 pt-4 border-t border-slate-700 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Key className="w-4 h-4 text-amber-400" />
                            <span className="text-slate-300 text-sm font-mono">{c.license_key}</span>
                          </div>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); copyKey(c.license_key); }} className="text-slate-400 hover:text-white" data-testid={`copy-key-${c.id}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-slate-500 text-xs">ID: {c.id}</p>
                        <p className="text-slate-500 text-xs">Org: {c.organization_id}</p>
                        <p className="text-slate-500 text-xs">Sede: {c.site_id}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <footer className="py-3 sm:py-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <img src={DISRUPTIVE_LOGO} alt="Disruptive" className="h-6 w-auto invert" />
          <span className="text-slate-500 text-xs">Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Disruptive Software</a>. Todos los derechos reservados.</span><span className="text-slate-500 text-xs ml-auto"><a href="mailto:info@disruptive-sw.com" className="hover:text-white">info@disruptive-sw.com</a></span>
        </div>
      </footer>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Server className="w-5 h-5 text-blue-400" />Registrar Conector</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" placeholder="Conector Sede Norte" data-testid="connector-name-input" /></div>
            {isSuperAdmin() && orgs.length > 0 ? (
              <>
                <div>
                  <Label className="text-slate-300">Organizacion</Label>
                  <Select value={form.organization_id} onValueChange={v => setForm({...form, organization_id: v, site_id: ''})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="connector-org-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Sede</Label>
                  <Select value={form.site_id} onValueChange={v => setForm({...form, site_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="connector-site-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {filteredSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <p className="text-slate-400 text-sm">Organizacion y sede se asignaran automaticamente.</p>
            )}
            <div><Label className="text-slate-300">Version</Label><Input value={form.version} onChange={e => setForm({...form, version: e.target.value})} className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="connector-version-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-connector-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
